import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { TimezoneSelect, DEFAULT_TIMEZONE } from '../ui/TimezoneSelect';
import {
  FiMapPin,
  FiClock,
  FiChevronDown,
  FiX,
  FiUpload,
  FiImage,
  FiCheck,
  FiSearch,
} from 'react-icons/fi';
import { type Area } from 'react-easy-crop';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FormLabel } from '../ui/FormLabel';
import { LocationSearchInput } from '../common/LocationSearchInput';
import { CoverCropModal } from '../common/CoverCropModal';
import { DateInput } from '../ui/DateInput';
import { useGetTags } from '../../api/services';
import type { LocationBody } from '../../services/locationService';
import { toast } from '../../utils/toast';
import { formatTagLabel } from '../../utils/tagLabel';
import { createCroppedImageFile, convertHeicToJpeg } from '../../utils/imageCrop';
import {
  UPLOAD_MAX_FILE_SIZE_BYTES,
  UPLOAD_MAX_FILE_SIZE_MB,
  getUploadSizeLimitMessage,
} from '../../utils/uploadValidation';

export interface GigFormData {
  title: string;
  description: string;
  additionalInfo: string;
  locationBody: LocationBody | null;
  budgetType: 'flat_rate' | 'range';
  budgetAmount: string;
  budgetMin: string;
  budgetMax: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  applicationStartDate: string;
  applicationStartTime: string;
  applicationEndDate: string;
  applicationEndTime: string;
  timezone: string;
  tagIds: number[];
  /** Newly selected local file to be uploaded to Cloudinary */
  image: File | null;
  /** Existing Cloudinary URL (pre-filled in edit mode) */
  existingImageUrl: string | null;
}

interface GigFormErrors {
  title?: string;
  locationBody?: string;
  startDate?: string;
  endDate?: string;
  applicationStartDate?: string;
  applicationEndDate?: string;
  description?: string;
  budgetAmount?: string;
  budgetMin?: string;
  budgetMax?: string;
  image?: string;
}

interface GigFormProps {
  initialData?: Partial<GigFormData>;
  onSubmit: (data: GigFormData) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onDelete?: () => void;
  title: string;
  submitLabel: string;
  showDelete?: boolean;
  isLoading?: boolean;
  isEditMode?: boolean;
  originalStartDate?: string;
}

export const GigForm: React.FC<GigFormProps> = ({
  initialData,
  onSubmit,
  onDirtyChange,
  onDelete,
  title,
  submitLabel,
  showDelete = false,
  isLoading = false,
  isEditMode = false,
  originalStartDate = '',
}) => {
  const normalizeLocation = (location: LocationBody | null) => {
    if (!location) return null;
    return {
      address: location.address ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      country: location.country ?? '',
      latitude: location.latitude ?? 0,
      longitude: location.longitude ?? 0,
    };
  };

  const secondaryLabelClass = 'text-zinc-600 text-[12px] uppercase tracking-wider';

  const [formData, setFormData] = useState<GigFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    additionalInfo: initialData?.additionalInfo || '',
    locationBody: initialData?.locationBody ?? null,
    budgetType: initialData?.budgetType || 'flat_rate',
    budgetAmount: initialData?.budgetAmount || '',
    budgetMin: initialData?.budgetMin || '',
    budgetMax: initialData?.budgetMax || '',
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
    startTime: initialData?.startTime || '',
    endTime: initialData?.endTime || '',
    applicationStartDate: initialData?.applicationStartDate || '',
    applicationStartTime: initialData?.applicationStartTime || '',
    applicationEndDate: initialData?.applicationEndDate || '',
    applicationEndTime: initialData?.applicationEndTime || '',
    timezone: initialData?.timezone || DEFAULT_TIMEZONE,
    tagIds: initialData?.tagIds ?? [],
    // image fields are tracked in separate state; these are placeholders
    image: null,
    existingImageUrl: initialData?.existingImageUrl ?? null,
  });

  const [errors, setErrors] = useState<GigFormErrors>({});
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  // ── tag state ──
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialData?.tagIds ?? []);
  const [tagSearch, setTagSearch] = useState('');
  const { data: allTags = [] } = useGetTags('');

  // ── image state ──
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(
    initialData?.existingImageUrl ?? null,
  );
  const imageInputRef = useRef<HTMLInputElement>(null);

  // ── crop state ──
  const [cropSourceFile, setCropSourceFile] = useState<File | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);

  const initialSnapshot = useMemo(
    () => ({
      title: initialData?.title || '',
      description: initialData?.description || '',
      additionalInfo: initialData?.additionalInfo || '',
      locationBody: normalizeLocation(initialData?.locationBody ?? null),
      budgetType: initialData?.budgetType || 'flat_rate',
      budgetAmount: initialData?.budgetAmount || '',
      budgetMin: initialData?.budgetMin || '',
      budgetMax: initialData?.budgetMax || '',
      startDate: initialData?.startDate || '',
      endDate: initialData?.endDate || '',
      startTime: initialData?.startTime || '',
      endTime: initialData?.endTime || '',
      applicationStartDate: initialData?.applicationStartDate || '',
      applicationStartTime: initialData?.applicationStartTime || '',
      applicationEndDate: initialData?.applicationEndDate || '',
      applicationEndTime: initialData?.applicationEndTime || '',
      timezone: initialData?.timezone || DEFAULT_TIMEZONE,
      tagIds: [...(initialData?.tagIds ?? [])].sort((a, b) => a - b),
      existingImageUrl: initialData?.existingImageUrl ?? null,
      hasNewImage: false,
    }),
    [initialData],
  );

  const handleImageSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed (JPEG, PNG, WEBP, GIF).');
        return;
      }
      if (file.size > UPLOAD_MAX_FILE_SIZE_BYTES) {
        toast.error(
          `${getUploadSizeLimitMessage(UPLOAD_MAX_FILE_SIZE_MB)} Please choose a smaller image and try again.`,
        );
        return;
      }

      // Convert HEIC to JPEG before cropping
      const convertedFile = await convertHeicToJpeg(file);
      const sourceUrl = URL.createObjectURL(convertedFile);
      if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
      setCropSourceFile(convertedFile);
      setCropSourceUrl(sourceUrl);
      setIsCropOpen(true);
    },
    [cropSourceUrl],
  );

  const closeCropModal = useCallback(() => {
    setIsCropOpen(false);
    setCropSourceFile(null);
    if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
    setCropSourceUrl(null);
  }, [cropSourceUrl]);

  const applyCrop = useCallback(
    async (cropAreaPixels: Area) => {
      if (!cropSourceFile || !cropSourceUrl) return;

      setIsApplyingCrop(true);
      try {
        const outputType = cropSourceFile.type.startsWith('image/')
          ? cropSourceFile.type
          : 'image/jpeg';
        const croppedFile = await createCroppedImageFile({
          imageSrc: cropSourceUrl,
          cropAreaPixels,
          fileName: cropSourceFile.name,
          outputType,
        });
        const nextPreview = URL.createObjectURL(croppedFile);

        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(croppedFile);
        setImagePreview(nextPreview);
        setExistingImageUrl(null);
        setErrors((prev) => ({ ...prev, image: undefined }));
        closeCropModal();
      } catch (error) {
        console.error('Gig image crop failed:', error);
        toast.error('Failed to crop image. Please try another image.');
      } finally {
        setIsApplyingCrop(false);
      }
    },
    [closeCropModal, cropSourceFile, cropSourceUrl, imagePreview],
  );

  const removeNewImage = useCallback(() => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  }, [imagePreview]);

  const removeExistingImage = () => setExistingImageUrl(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof GigFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLocationSelected = (location: LocationBody | null) => {
    setFormData((prev) => ({ ...prev, locationBody: location }));
    if (location) {
      setErrors((prev) => ({ ...prev, locationBody: undefined }));
      setIsLocationModalOpen(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: GigFormErrors = {};
    let hasRequiredMissing = false;

    if (!formData.title.trim()) {
      newErrors.title = 'This field is required.';
      hasRequiredMissing = true;
    }
    if (!formData.locationBody) {
      newErrors.locationBody = 'This field is required.';
      hasRequiredMissing = true;
    }
    if (!formData.description.trim()) {
      newErrors.description = 'This field is required.';
      hasRequiredMissing = true;
    }
    if (!formData.startDate) {
      newErrors.startDate = 'This field is required.';
      hasRequiredMissing = true;
    }
    if (!formData.applicationStartDate) {
      newErrors.applicationStartDate = 'This field is required.';
      hasRequiredMissing = true;
    }

    // Future date validation (only if a start date is provided)
    if (formData.startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPastDate = new Date(formData.startDate) < today;
      const isAllowedOriginalPastDate =
        isEditMode && !!originalStartDate && formData.startDate === originalStartDate;

      if (isPastDate && !isAllowedOriginalPastDate) {
        newErrors.startDate = 'Gig date must be in the future.';
      }
    }

    // Start date must be before end date
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        newErrors.endDate = 'End date must be after the start date.';
      }
    }

    // Application start date must be before application end date
    if (formData.applicationStartDate && formData.applicationEndDate) {
      if (new Date(formData.applicationEndDate) < new Date(formData.applicationStartDate)) {
        newErrors.applicationEndDate =
          'Application end date must be after the application start date.';
      }
    }

    // Budget validation – all fields are optional; defaults to 0 when empty.
    if (formData.budgetType === 'flat_rate') {
      if (formData.budgetAmount.trim()) {
        const amount = parseFloat(formData.budgetAmount);
        if (isNaN(amount) || amount < 0) {
          newErrors.budgetAmount = 'Budget must be a valid amount.';
        }
      }
    } else {
      const minRaw = formData.budgetMin.trim();
      const maxRaw = formData.budgetMax.trim();
      const min = parseFloat(formData.budgetMin);
      const max = parseFloat(formData.budgetMax);
      if (minRaw && (isNaN(min) || min < 0)) {
        newErrors.budgetMin = 'Budget must be a valid amount.';
      }
      if (maxRaw && (isNaN(max) || max < 0)) {
        newErrors.budgetMax = 'Budget must be a valid amount.';
      }
      // Validate range order only when both values are provided and individually valid
      if (minRaw && maxRaw && !newErrors.budgetMin && !newErrors.budgetMax && min > max) {
        newErrors.budgetMin = 'Starting amount cannot be greater than the ending amount.';
      }
    }

    if (hasRequiredMissing) toast.error('Please fill in all required fields.');
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ ...formData, tagIds: selectedTagIds, image: imageFile, existingImageUrl });
  };

  const isRangeType = formData.budgetType === 'range';

  useEffect(() => {
    if (!onDirtyChange) return;

    const currentSnapshot = {
      title: formData.title,
      description: formData.description,
      additionalInfo: formData.additionalInfo,
      locationBody: normalizeLocation(formData.locationBody),
      budgetType: formData.budgetType,
      budgetAmount: formData.budgetAmount,
      budgetMin: formData.budgetMin,
      budgetMax: formData.budgetMax,
      startDate: formData.startDate,
      endDate: formData.endDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      timezone: formData.timezone,
      tagIds: [...selectedTagIds].sort((a, b) => a - b),
      existingImageUrl,
      hasNewImage: Boolean(imageFile),
    };

    onDirtyChange(JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot));
  }, [onDirtyChange, formData, selectedTagIds, existingImageUrl, imageFile, initialSnapshot]);

  const locationLabel = formData.locationBody
    ? formData.locationBody.address ||
      [formData.locationBody.city, formData.locationBody.state].filter(Boolean).join(', ')
    : null;

  return (
    <form noValidate onSubmit={handleSubmit} className="max-w-[1440px] mx-auto px-4 py-8">
      {/* Hidden image input */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
        className="hidden"
        onChange={(e) => handleImageSelect(e.target.files)}
        onClick={(e) => ((e.target as HTMLInputElement).value = '')}
      />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <h1 className="text-white text-2xl sm:text-[32px] font-bold">{title}</h1>
        <div className="hidden sm:flex items-center gap-4 text-zinc-500 text-[14px]">
          <Button
            type="submit"
            size="lg"
            isLoading={isLoading}
            className="rounded-full px-8 py-3 bg-white text-black hover:bg-zinc-200 focus:ring-0"
          >
            {submitLabel}
          </Button>
          {showDelete && onDelete && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="rounded-full px-8 py-3 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Left Column */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <FormLabel required>Title</FormLabel>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter Gig title"
              className={`h-14 bg-zinc-900/40 ${errors.title ? 'border-red-500/60' : 'border-white/25'}`}
            />
            {errors.title && <p className="text-red-400 text-[12px] mt-1">{errors.title}</p>}
          </div>

          <div className="flex flex-col gap-3">
            <FormLabel required>Description</FormLabel>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add description..."
              className={`w-full h-32 bg-zinc-900/40 border rounded-2xl p-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none ${
                errors.description ? 'border-red-500/60' : 'border-white/25'
              }`}
            />
            {errors.description && (
              <p className="text-red-400 text-[12px] mt-1">{errors.description}</p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <FormLabel optional>Additional information</FormLabel>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder="Add other information..."
              className="w-full h-48 bg-zinc-900/40 border border-white/25 rounded-2xl p-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
            />
          </div>

          {/* Tags */}
          <div className="flex flex-col gap-4">
            <FormLabel optional>Tags</FormLabel>

            {/* Search */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center bg-zinc-900/40 border border-white/25 rounded-2xl px-4 h-12 focus-within:border-blue-500/40 transition-all">
                <FiSearch size={15} className="text-zinc-500 shrink-0" />
                <input
                  type="text"
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  placeholder="Search tags..."
                  className="ml-3 bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                />
                {tagSearch && (
                  <button
                    type="button"
                    onClick={() => setTagSearch('')}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
              <p className="text-zinc-500 text-[12px] px-1">
                Search and select a tag that fits the service rendered
              </p>
            </div>

            {/* Grouped pills */}
            {(() => {
              const filtered = allTags.filter((t) =>
                t.name.toLowerCase().includes(tagSearch.toLowerCase()),
              );

              const selectedTags = allTags.filter((t) => selectedTagIds.includes(t.id));

              const groups: Record<string, typeof allTags> = {};
              for (const tag of filtered) {
                const rawCategory = tag.category?.trim();
                const cat =
                  !rawCategory || /^others?$/i.test(rawCategory) ? 'Tag List' : rawCategory;
                (groups[cat] ??= []).push(tag);
              }

              const entries = Object.entries(groups);

              if (filtered.length === 0 && tagSearch) {
                return (
                  <p className="text-zinc-500 text-[13px]">
                    No tags found for &ldquo;{tagSearch}&rdquo;
                  </p>
                );
              }

              return (
                <div className="flex flex-col gap-4">
                  {selectedTags.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <p className="text-blue-400 text-[11px] font-semibold uppercase tracking-widest">
                        Selected Tags
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTags.map((tag) => (
                          <button
                            key={`selected-${tag.id}`}
                            type="button"
                            onClick={() =>
                              setSelectedTagIds((prev) => prev.filter((id) => id !== tag.id))
                            }
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-all border bg-zinc-800 border-white/20 text-white"
                          >
                            <FiCheck size={12} strokeWidth={3} />
                            {formatTagLabel(tag.name)}
                          </button>
                        ))}
                      </div>
                      <div className="w-full h-px bg-white/5 my-1" />
                    </div>
                  )}

                  <div className="h-64 overflow-y-auto pr-1 flex flex-col gap-5 [scrollbar-width:thin] [scrollbar-color:var(--color-zinc-700)_transparent]">
                    {entries.map(([category, tags]) => (
                      <div key={category} className="flex flex-col gap-3">
                        <p className="sticky top-0 z-20 mb-3 border-b border-white/10 pb-2 backdrop-blur-sm text-zinc-500 text-[11px] font-semibold uppercase tracking-widest">
                          {category}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => {
                            const isSelected = selectedTagIds.includes(tag.id);
                            const normalizedTagName = /^others?$/i.test(tag.name.trim())
                              ? 'Tag List'
                              : formatTagLabel(tag.name);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() =>
                                  setSelectedTagIds((prev) =>
                                    isSelected
                                      ? prev.filter((id) => id !== tag.id)
                                      : [...prev, tag.id],
                                  )
                                }
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-all border ${
                                  isSelected
                                    ? 'bg-zinc-800 border-white/20 text-white'
                                    : 'bg-transparent border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200'
                                }`}
                              >
                                {isSelected && <FiCheck size={12} strokeWidth={3} />}
                                {normalizedTagName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-[12px] text-center italic mt-1">
                    Scroll to view tag list
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-8">
          {/* Image Upload */}
          <div className="flex flex-col gap-3">
            <FormLabel optional>Cover Image</FormLabel>
            <p className="text-zinc-600 text-[11px] -mt-1">Recommended: 16:9 aspect ratio</p>

            {/* Preview — shows either the new file preview or the existing URL */}
            {imagePreview || existingImageUrl ? (
              <div className="relative w-full aspect-video rounded-3xl overflow-hidden group bg-zinc-950 border border-white/5">
                <img
                  src={imagePreview ?? existingImageUrl!}
                  alt="Gig cover"
                  className="w-full h-full object-contain"
                />

                {/* Always visible remove button */}
                <button
                  type="button"
                  onClick={imagePreview ? removeNewImage : removeExistingImage}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-red-500 hover:text-white transition-colors shadow-lg z-10"
                  title="Remove image"
                >
                  <FiX size={16} />
                </button>

                {/* Replace button */}
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white text-[12px] font-medium hover:bg-white/20 transition-colors shadow-lg z-10"
                >
                  <FiUpload size={14} />
                  Replace
                </button>
              </div>
            ) : (
              /* Upload drop zone */
              <div
                onClick={() => imageInputRef.current?.click()}
                className={`w-full aspect-video rounded-3xl border-2 border-dashed bg-zinc-900/40 flex flex-col items-center justify-center gap-4 cursor-pointer hover:bg-white/5 transition-all ${
                  errors.image ? 'border-red-500/60' : 'border-white/10 hover:border-white/20'
                }`}
              >
                <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center text-zinc-400">
                  <FiImage size={26} />
                </div>
                <div className="text-center px-4">
                  <p className="text-white font-medium text-base">Add a cover image</p>
                  <p className="text-zinc-500 text-xs mt-1">JPEG · PNG · WEBP — 1 image max</p>
                </div>
              </div>
            )}

            {errors.image && <p className="text-red-400 text-[12px]">{errors.image}</p>}
          </div>

          <div className="flex flex-col gap-3">
            <FormLabel required>Location</FormLabel>
            <div
              className={`relative flex items-center bg-zinc-900/40 border rounded-2xl px-4 h-14 cursor-pointer hover:bg-white/5 transition-all group ${
                errors.locationBody ? 'border-red-500/60' : 'border-white/25'
              }`}
              onClick={() => setIsLocationModalOpen(true)}
            >
              <FiMapPin className="text-blue-500 mr-3 shrink-0" />
              <span
                className={`text-[15px] truncate min-w-0 ${locationLabel ? 'text-white' : 'text-zinc-600'}`}
              >
                {locationLabel || 'Select location'}
              </span>
              <FiChevronDown className="ml-auto text-zinc-500 group-hover:text-white transition-colors shrink-0" />
            </div>
            {errors.locationBody && (
              <p className="text-red-400 text-[12px] mt-1">{errors.locationBody}</p>
            )}
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-white text-lg font-semibold flex items-center gap-2">
              Gig Date & Time
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel required labelClassName={secondaryLabelClass}>
                  Start Date
                </FormLabel>
                <DateInput
                  name="startDate"
                  value={formData.startDate}
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'startDate', value },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  className={`h-14 bg-zinc-900/40 ${errors.startDate ? 'border-red-500/60' : 'border-white/25'}`}
                />
                {errors.startDate && <p className="text-red-400 text-[12px]">{errors.startDate}</p>}
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel labelClassName={secondaryLabelClass}>End Date</FormLabel>
                <DateInput
                  name="endDate"
                  value={formData.endDate}
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'endDate', value },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  className={`h-14 bg-zinc-900/40 ${errors.endDate ? 'border-red-500/60' : 'border-white/25'}`}
                />
                {errors.endDate && <p className="text-red-400 text-[12px]">{errors.endDate}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel labelClassName={secondaryLabelClass}>Start Time</FormLabel>
                <Input
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={handleChange}
                  placeholder="Start time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/40 border-white/25 scheme-dark"
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel labelClassName={secondaryLabelClass}>End Time</FormLabel>
                <Input
                  name="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={handleChange}
                  placeholder="End time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/40 border-white/25 scheme-dark"
                />
              </div>
            </div>

            {/* Application Period */}
            <div className="flex flex-col gap-3">
              <h3 className="text-white text-base font-semibold flex items-center gap-2">
                Application Period
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormLabel required labelClassName={secondaryLabelClass}>
                    Application Start Date
                  </FormLabel>
                  <DateInput
                    name="applicationStartDate"
                    value={formData.applicationStartDate}
                    onChange={(value) =>
                      handleChange({
                        target: { name: 'applicationStartDate', value },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    className={`h-14 bg-zinc-900/40 ${errors.applicationStartDate ? 'border-red-500/60' : 'border-white/25'}`}
                  />
                  {errors.applicationStartDate && (
                    <p className="text-red-400 text-[12px]">{errors.applicationStartDate}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <FormLabel labelClassName={secondaryLabelClass}>Application End Date</FormLabel>
                  <DateInput
                    name="applicationEndDate"
                    value={formData.applicationEndDate}
                    onChange={(value) =>
                      handleChange({
                        target: { name: 'applicationEndDate', value },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    className={`h-14 bg-zinc-900/40 ${errors.applicationEndDate ? 'border-red-500/60' : 'border-white/25'}`}
                  />
                  {errors.applicationEndDate && (
                    <p className="text-red-400 text-[12px]">{errors.applicationEndDate}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormLabel labelClassName={secondaryLabelClass}>Application Start Time</FormLabel>
                  <Input
                    name="applicationStartTime"
                    type="time"
                    value={formData.applicationStartTime}
                    onChange={handleChange}
                    placeholder="Start time"
                    icon={FiClock}
                    className="h-14 bg-zinc-900/40 border-white/25 scheme-dark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <FormLabel labelClassName={secondaryLabelClass}>Application End Time</FormLabel>
                  <Input
                    name="applicationEndTime"
                    type="time"
                    value={formData.applicationEndTime}
                    onChange={handleChange}
                    placeholder="End time"
                    icon={FiClock}
                    className="h-14 bg-zinc-900/40 border-white/25 scheme-dark"
                  />
                </div>
              </div>
            </div>

            {/* Timezone */}
            <div className="flex flex-col gap-2">
              <FormLabel required labelClassName={secondaryLabelClass}>
                Timezone
              </FormLabel>
              <TimezoneSelect
                value={formData.timezone}
                onChange={(val) =>
                  handleChange({
                    target: { name: 'timezone', value: val },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
            </div>
          </div>

          {/* Budget */}
          <div className="flex flex-col gap-3">
            <FormLabel optional>Budget</FormLabel>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Budget type selector */}
              <div className="relative w-full sm:w-1/3">
                <select
                  name="budgetType"
                  value={formData.budgetType}
                  onChange={handleChange}
                  className="w-full h-14 bg-zinc-900/40 border border-white/25 rounded-2xl px-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all cursor-pointer"
                >
                  <option value="flat_rate">Flat Rate</option>
                  <option value="range">Range</option>
                </select>
                <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>

              {/* Conditional amount fields */}
              {isRangeType ? (
                <div className="flex flex-1 gap-3 items-center">
                  <div className="relative flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center rounded-l-2xl border-r border-white/15 bg-zinc-800/70 text-[15px] font-semibold text-white/90">
                      $
                    </div>
                    <input
                      name="budgetMin"
                      value={formData.budgetMin}
                      onChange={handleChange}
                      placeholder="Min"
                      inputMode="decimal"
                      className={`w-full h-14 bg-zinc-900/40 border rounded-2xl pl-16 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
                        errors.budgetMin ? 'border-red-500/60' : 'border-white/25'
                      }`}
                    />
                  </div>
                  <span className="text-zinc-500 shrink-0">–</span>
                  <div className="relative flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center rounded-l-2xl border-r border-white/15 bg-zinc-800/70 text-[15px] font-semibold text-white/90">
                      $
                    </div>
                    <input
                      name="budgetMax"
                      value={formData.budgetMax}
                      onChange={handleChange}
                      placeholder="Max"
                      inputMode="decimal"
                      className={`w-full h-14 bg-zinc-900/40 border rounded-2xl pl-16 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
                        errors.budgetMax ? 'border-red-500/60' : 'border-white/25'
                      }`}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative flex-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center rounded-l-2xl border-r border-white/15 bg-zinc-800/70 text-[15px] font-semibold text-white/90">
                    $
                  </div>
                  <input
                    name="budgetAmount"
                    value={formData.budgetAmount}
                    onChange={handleChange}
                    placeholder="e.g. 500"
                    inputMode="decimal"
                    className={`w-full h-14 bg-zinc-900/40 border rounded-2xl pl-16 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
                      errors.budgetAmount ? 'border-red-500/60' : 'border-white/25'
                    }`}
                  />
                </div>
              )}
            </div>
            {(errors.budgetAmount || errors.budgetMin || errors.budgetMax) && (
              <p className="text-red-400 text-[12px]">
                {errors.budgetAmount || errors.budgetMin || errors.budgetMax}
              </p>
            )}
            <p className="text-zinc-600 text-[12px]">
              {isRangeType
                ? 'Enter a min and max budget range for this gig'
                : 'Enter a fixed fee for this gig'}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Actions */}
      <div className="flex sm:hidden flex-col gap-4 mt-12 pt-8 border-t border-white/5">
        <Button
          type="submit"
          size="lg"
          isLoading={isLoading}
          className="w-full rounded-full h-14 bg-white text-black hover:bg-zinc-200 focus:ring-0"
        >
          {submitLabel}
        </Button>
        {showDelete && onDelete && (
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full rounded-full h-14 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0"
            onClick={onDelete}
          >
            Delete
          </Button>
        )}
      </div>

      {isLocationModalOpen && (
        <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full sm:max-w-lg bg-[#0c0c0c] border border-white/10 rounded-t-4xl sm:rounded-4xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h3 className="text-white text-lg font-semibold">Select Location</h3>
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(false)}
                className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="px-4 pb-6">
              <LocationSearchInput
                onLocationSelected={handleLocationSelected}
                initialLocation={formData.locationBody}
                placeholder="Search city, address…"
              />
            </div>
          </div>
        </div>
      )}

      <CoverCropModal
        isOpen={isCropOpen}
        imageSrc={cropSourceUrl}
        isSubmitting={isApplyingCrop}
        aspect={16 / 9}
        title="Adjust gig cover"
        description="Drag to choose what appears as your gig cover"
        onCancel={closeCropModal}
        onConfirm={applyCrop}
      />
    </form>
  );
};
